import { ESC_POS_Printer_Config, LabelPrintParam, Printer, USBDevice } from '../global';
import { Order, OrderSku, PosOrderConfig } from '@/models/order';
import { SKU, SKUSpecification } from '@/models/sku';
import {
  STORAGE_KEY_POS_ORDER_CONFIG,
  STORAGE_KEY_POS_PRIMARY_ESCPOS_PRINTER,
  STORAGE_KEY_POS_PRIMARY_ESCPOS_PRINTER_CONFIG,
  STORAGE_KEY_POS_PRIMARY_TSPL_PRINTER,
  STORAGE_KEY_POS_PRIMARY_TSPL_PRINTER_CONFIG,
} from './storage';
import { message } from '@/components/Message';
import { addUsbDeviceChangeListener } from '@/utils/usb';
import { useLocalStorage } from 'react-use';
import { useCallback, useContext } from 'react';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import dayjs from 'dayjs';
import { DateTimeShortFormat } from '@/models/common';

export enum TSPL_Direction {
  FORWARD = 0,
  BACKWARD = 1,
}

export enum TSPL_Text_Mirror {
  NORMAL = 0,
  MIRROR = 1,
}

export enum TSPL_Print_Type {
  TEXT = 0,
  IMAGE = 1,
  QR = 2,
  BARCODE = 3,
}

export enum TSPL_Print_Align {
  LEFT = 0,
  CENTER = 1,
  RIGHT = 2,
}

export const isEscPosPrinter = () => {};

export const getPrinterList = () => {
  try {
    return JSON.parse(window.__POS_Printer__.findUSBPrinters()) as USBDevice[];
  } catch (e) {
    return [];
  }
};

let activePrinter: Printer;
let activePrinterConfig: ESC_POS_Printer_Config;

export const resetPrinter = () => {
  activePrinter = null;
};

// 设备变化: 如果 activePrinter 被移出 -> 重置
addUsbDeviceChangeListener(deviceList => {
  const primaryEscPosId = localStorage.getItem(STORAGE_KEY_POS_PRIMARY_ESCPOS_PRINTER);
  if (!primaryEscPosId) return;

  for (let device of deviceList) {
    if (`${device.mVendorId}-${device.mProductId}` === primaryEscPosId) {
      return;
    }
  }
  resetPrinter();
});

export const getPrinter = () => {
  if (activePrinter) return activePrinter;

  const primaryEscPosId = JSON.parse(localStorage.getItem(STORAGE_KEY_POS_PRIMARY_ESCPOS_PRINTER));

  activePrinterConfig = JSON.parse(
    localStorage.getItem(STORAGE_KEY_POS_PRIMARY_ESCPOS_PRINTER_CONFIG)
  ) as ESC_POS_Printer_Config;

  return (activePrinter = window.__POS_Printer__.getPrinter(
    +primaryEscPosId.split('-')[0],
    +primaryEscPosId.split('-')[1],
    activePrinterConfig.dpi,
    activePrinterConfig.printWidth,
    activePrinterConfig.charPerLine,
    activePrinterConfig.encoding,
    activePrinterConfig.charsetId
  ));
};

export const openCashDraw = () => {
  const printer = getPrinter();
  if (!printer) {
    message.error('小票机未连接');
    return;
  }
  printer.openCashDraw(activePrinterConfig.cashDrawPin);
};

export const print = (text: string, openCashDraw = false) => {
  const printer = getPrinter();
  if (!printer) {
    message.error('小票机未连接');
    return;
  }

  if (activePrinterConfig.header) {
    text =
      `[C]<font size='big'>${activePrinterConfig.header}</font>
[C]
[C]================================
[C]
` + text;
  }

  if (activePrinterConfig.footer) {
    text += `
[C]--------------------------------
[C]${activePrinterConfig.footer}`;
  }

  if (printer) {
    // if (openCashDraw) {
    //   printer.openCashDraw(activePrinterConfig.cashDrawPin);
    // }
    printer.print(
      text,
      activePrinterConfig.feedDistance,
      openCashDraw ? activePrinterConfig.cashDrawPin : 0,
      activePrinterConfig.autoCut
    );
    // .feed(activePrinterConfig.feedDistance);
    // if (activePrinterConfig.autoCut) {
    //   printer.cut();
    // }
  }
};

export const usePrintOrder = () => {
  const [{ poi }] = useContext(BaseInfoContext);
  return useCallback(
    (order: Order, specifications: SKUSpecification[] | OrderSku[], openCashDraw = false) => {
      let str = `[L]订单号: ${order.orderSn}
[L]\n`;

      specifications.forEach(specification => {
        const goodsName = specification.goodsName ? specification.goodsName : specification.name;
        const specificationName = specification.specificationName
          ? specification.specificationName
          : specification.name;

        str += `[L]${goodsName} ${specificationName ? `[C]${specificationName}` : ''} [R] x${
          specification.number
        }\n`;
      });
      str += `[C]--------------------------------
[R]实付金额 :[R]${order.actualPrice}
[C]================================
[L]
[C]微信扫码绑定订单
[L]
[C]<qrcode size='29'>https://admin.veeyung.com/admin/s/${poi.id}/f/w?oid=${order.orderSn}</qrcode>
[C]`;
      print(str, openCashDraw);
    },
    []
  );
};

export const printLabel = (content: LabelPrintParam[] = []) => {
  const primaryTSPL = JSON.parse(localStorage.getItem(STORAGE_KEY_POS_PRIMARY_TSPL_PRINTER));
  const primaryTSPLConfig = localStorage.getItem(STORAGE_KEY_POS_PRIMARY_TSPL_PRINTER_CONFIG);

  window.__POS_Printer__.printLabel(
    +primaryTSPL.split('-')[0],
    +primaryTSPL.split('-')[1],

    primaryTSPLConfig,

    JSON.stringify(content.filter(v => !!v && v.content !== undefined && v.content.length))
  );
};

export const isPrintOrderLabelOpened = () => {
  try {
    const posOrderConfig = JSON.parse(
      localStorage.getItem(STORAGE_KEY_POS_ORDER_CONFIG)
    ) as PosOrderConfig;
    return posOrderConfig.printLabel;
  } catch (e) {
    return false;
  }
};

export const printGoodsLabel = (goods: SKU) => {
  printLabel([
    {
      type: TSPL_Print_Type.BARCODE,
      content: goods.goodsSn,
      top: 0,
      left: 0,
      height: 10,
      width: 30,
      marginBottom: 1,
      align: TSPL_Print_Align.CENTER,
    },
    {
      content: `${goods.goodsSn}`,
      type: TSPL_Print_Type.TEXT,
      align: TSPL_Print_Align.CENTER,
      marginBottom: 1.5,
    },
    {
      content: `${goods.name}`,
      type: TSPL_Print_Type.TEXT,
      align: TSPL_Print_Align.CENTER,
    },
  ]);
};

export const usePrintOrderLabel = (cancelOnManuelPrint = false) => {
  const [orderConfig, setOrderConfig] = useLocalStorage<PosOrderConfig>(
    STORAGE_KEY_POS_ORDER_CONFIG,
    {} as PosOrderConfig
  );

  return useCallback(
    (specifications: SKUSpecification[] | OrderSku[], order: Order) => {
      if (cancelOnManuelPrint && orderConfig.printLabelManual) {
        return;
      }
      if (orderConfig.printLabel && specifications && specifications.length) {
        const printSpecifications = [];
        specifications.forEach((specification, index) => {
          if (specification.number) {
            for (let i = 0; i < specification.number; i++) {
              printSpecifications.push(specification);
            }
          } else {
            printSpecifications.push(specification);
          }
        });

        printSpecifications.forEach((specification, index) => {
          printLabel([
            orderConfig.printLabelTitleEnabled && orderConfig.printLabelTitle
              ? {
                  content: orderConfig.printLabelTitle,
                  type: TSPL_Print_Type.TEXT,
                  align: TSPL_Print_Align.CENTER,
                  scale: 2,
                  marginBottom: 1.5,
                }
              : null,
            orderConfig.printLabelGoodsName
              ? {
                  content: specification.goodsName,
                  type: TSPL_Print_Type.TEXT,
                }
              : null,
            orderConfig.printLabelSpecificationName && specification.name
              ? {
                  content: specification.name,
                  type: TSPL_Print_Type.TEXT,
                }
              : null,
            orderConfig.printLabelPrice
              ? {
                  content: `${specification.price}元`,
                  type: TSPL_Print_Type.TEXT,
                  align: TSPL_Print_Align.LEFT,
                }
              : null,
            orderConfig.printLabelIndex
              ? {
                  content: `(${index + 1}/${printSpecifications.length})`,
                  type: TSPL_Print_Type.TEXT,
                  align: TSPL_Print_Align.LEFT,
                }
              : null,
            orderConfig.printLabelDateTime
              ? {
                  content: `${dayjs().format(DateTimeShortFormat)}`,
                  type: TSPL_Print_Type.TEXT,
                  align: TSPL_Print_Align.LEFT,
                }
              : null,
            orderConfig.printLabelOrderQr
              ? {
                  content: `id:order/${order.orderSn}`,
                  type: TSPL_Print_Type.QR,
                  left: 0.7,
                  top: 0.55,
                  qrSize: 4,
                }
              : null,
          ]);
        });
      }
    },
    [orderConfig]
  );
};
